<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'select'])

// Sample services data - you can move this to a store later
const services = ref([
  {
    id: 1,
    name: 'Monthly Subscription',
    description: 'Access to 120 games',
    price: 960.00,
    category: 'subscription'
  },
  {
    id: 2,
    name: 'Extra Matches',
    description: 'Los Angeles Lakers vs Boston Celtics\nNBA',
    price: 12.00,
    category: 'match'
  },
  {
    id: 3,
    name: 'Season Pass',
    description: 'Full season access to all games',
    price: 2400.00,
    category: 'subscription'
  }
])

const newService = ref({
  name: '',
  description: '',
  price: 0,
  category: 'match'
})

const isAddingNew = ref(false)

const addService = () => {
  if (!newService.value.name || !newService.value.description || !newService.value.price) {
    return
  }
  
  services.value.push({
    id: services.value.length + 1,
    ...newService.value
  })
  newService.value = { name: '', description: '', price: 0, category: 'match' }
  isAddingNew.value = false
}

const selectService = (service) => {
  emit('select', {
    description: service.name,
    details: service.description,
    quantity: 1,
    price: service.price
  })
}

const deleteService = (index) => {
  services.value.splice(index, 1)
}

// Filter services
const selectedCategory = ref('all')
const filteredServices = computed(() => {
  if (selectedCategory.value === 'all') return services.value
  return services.value.filter(service => service.category === selectedCategory.value)
})

const formatPrice = (price) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(price)
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
      <!-- Header -->
      <div class="px-6 py-4 border-b flex justify-between items-center">
        <h2 class="text-xl font-bold text-gray-800">Manage Services</h2>
        <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Services List -->
        <div class="mb-4">
          <div class="flex justify-between items-center mb-4">
            <div class="flex items-center gap-4">
              <h3 class="text-lg font-medium">Available Services</h3>
              <select 
                v-model="selectedCategory"
                class="px-3 py-1 border rounded-md text-sm"
              >
                <option value="all">All Categories</option>
                <option value="subscription">Subscriptions</option>
                <option value="match">Single Matches</option>
              </select>
            </div>
            <button 
              @click="isAddingNew = true"
              class="text-sm text-pink-500 hover:text-pink-600"
            >
              <i class="fas fa-plus mr-1"></i> Add New Service
            </button>
          </div>

          <!-- Add New Service Form -->
          <div v-if="isAddingNew" class="bg-gray-50 p-4 rounded-lg mb-4">
            <div class="grid gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                <input 
                  v-model="newService.name"
                  type="text"
                  class="form-input"
                  placeholder="e.g. Monthly Subscription"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input 
                  v-model="newService.description"
                  type="text"
                  class="form-input"
                  placeholder="e.g. Access to 120 games"
                >
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <input 
                    v-model.number="newService.price"
                    type="number"
                    step="0.01"
                    class="form-input"
                    placeholder="0.00"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select 
                    v-model="newService.category"
                    class="form-input"
                  >
                    <option value="subscription">Subscription</option>
                    <option value="match">Single Match</option>
                  </select>
                </div>
              </div>
              <div class="flex justify-end gap-2">
                <button 
                  @click="isAddingNew = false"
                  class="px-3 py-1 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button 
                  @click="addService"
                  class="px-3 py-1 bg-pink-500 text-white rounded hover:bg-pink-600"
                  :disabled="!newService.name || !newService.description || !newService.price"
                >
                  Add Service
                </button>
              </div>
            </div>
          </div>

          <!-- Services Table -->
          <div class="bg-white border rounded-lg">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-gray-600">Service</th>
                  <th class="px-4 py-3 text-right text-gray-600">Price</th>
                  <th class="px-4 py-3 text-center text-gray-600 w-32">Category</th>
                  <th class="px-4 py-3 text-center text-gray-600 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(service, index) in filteredServices" :key="service.id" class="border-t">
                  <td class="px-4 py-3">
                    <div class="font-medium text-gray-800">{{ service.name }}</div>
                    <div class="text-sm text-gray-500">{{ service.description }}</div>
                  </td>
                  <td class="px-4 py-3 text-right">
                    {{ formatPrice(service.price) }}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span 
                      class="px-2 py-1 rounded-full text-xs"
                      :class="{
                        'bg-blue-100 text-blue-700': service.category === 'subscription',
                        'bg-green-100 text-green-700': service.category === 'match'
                      }"
                    >
                      {{ service.category }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex justify-center gap-2">
                      <button 
                        @click="selectService(service)"
                        class="text-pink-500 hover:text-pink-600"
                        title="Add to Invoice"
                      >
                        <i class="fas fa-plus-circle"></i>
                      </button>
                      <button 
                        @click="deleteService(index)"
                        class="text-red-500 hover:text-red-600"
                        title="Delete Service"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
         focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500;
}
</style> 